// Considered: lifting `deprecated/a2ui-prototype`'s `A2UISessionProvider`/`ChatPanel`
// verbatim — rejected because its SSE frame vocabulary (`a2ui`/`sphereChanged`, routed into a
// SurfaceStore/DataModelStore) doesn't match composer-agent-service's (`a2uiProposal`/`chatText`/
// `progress`/`error`/`done`, routed into the composer's ghost buffer). The state-machine SHAPE
// (messages/isLoading/activeTool/connectionError/retry) is mirrored here; the transport and frame
// routing are composer-specific (plan ratified tiering: Organism, since it owns SSE transport +
// retry — a Molecule doesn't fetch).
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { GhostProposal } from '../../../composer/composerStore';
import {
  probeComposerAgentAvailability,
  proposalToGhostComponents,
  sendComposerAgentMessage,
  confirmComposerContext,
  ComposerAgentNetworkError,
  ComposerAgentRequestError,
  type ComposerAgentScope,
  type ComposerAgentTarget,
  type ComposerAgentQuestionSet,
  type ComposerAgentContextConfirmation,
  type ComposerAgentTypeEditProposal,
  type ComposerGhostProvenance,
  type ComposerTypeCritique,
} from '../../../composer/agentTransport';
import AgentMessageMolecule, { type AgentMessageParity, type AgentThreadItem, type ContextConfirmationErrorCode } from '../../molecules/composer/AgentMessageMolecule';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './AgentThreadOrganism.scss';

/** One glyph per critique severity, beside the chip's tone (ux-craft A6). */
const CRITIQUE_SEVERITY_ICON: Record<'info' | 'warn' | 'error', string> = {
  info: 'pi-info-circle',
  warn: 'pi-exclamation-triangle',
  error: 'pi-times-circle',
};

/**
 * The service emits STABLE error CODES over SSE, never raw internal error text — see
 * `routes/chat/stream.ts`. Anything unrecognised falls back to the generic message
 * rather than being rendered verbatim, so a new server-side code can never leak
 * untranslated internals into the chat pane.
 */
const AGENT_ERROR_CODES = ['daily_cap_reached', 'proposal_rejected', 'agent_failed', 'not_configured', 'unreachable'] as const;

function translateAgentError(t: (key: string) => string, code: string): string {
  const known = (AGENT_ERROR_CODES as readonly string[]).includes(code) ? code : 'unknown';
  return t(`composer.agent.error.${known}`);
}

/**
 * `POST /context/confirm` reports its three outcomes by status — 409 the name is already in use,
 * 403 the caller lacks `edit` authority on their organization sphere, anything else generic. Each
 * gets its own copy, because "try again" is wrong advice for the first two.
 */
function contextConfirmErrorCode(err: unknown): ContextConfirmationErrorCode {
  if (err instanceof ComposerAgentRequestError) {
    if (err.status === 409) return 'nameTaken';
    if (err.status === 403) return 'forbidden';
  }
  return 'unknown';
}

export interface AgentThreadOrganismProps {
  /** `composer-agent-service`'s base URL. Absent (no dynamic config / env var set) renders the unavailable state without ever probing. */
  endpoint?: string;
  getToken?: () => Promise<string | undefined>;
  target: ComposerAgentTarget;
  scope?: ComposerAgentScope;
  draftDigest?: string;
  /**
   * Adds a proposal to the composer's ghost buffer — the caller (composer store owner) is the
   * source of truth for `state.ghosts`. Optional (EAINT-862 Phase 3): a `context` target never
   * emits an `a2uiProposal` frame, so a Context Builder host that only ever passes `target.kind:
   * 'context'` has nothing to wire these to.
   */
  onProposeGhost?: (ghost: GhostProposal, rationale: string) => void;
  onAcceptGhost?: (ghostId: string) => void;
  onRejectGhost?: (ghostId: string) => void;
  /** Fires once per user turn sent — analytics hook (`composer_agent_message_sent`). */
  onMessageSent?: () => void;
  /** Fires once per ghost resolved — analytics hook (`composer_ghost_accepted` / `composer_ghost_rejected`). */
  onGhostAccepted?: (componentCount: number) => void;
  onGhostRejected?: (componentCount: number) => void;
  /** Fires once per context actually created — analytics hook (`composer_context_created`). */
  onContextConfirmed?: (systemName: string) => void;
  /** Fires once per context proposal dismissed — analytics hook (`composer_context_cancelled`). */
  onContextCancelled?: () => void;
  /**
   * EAINT-862 Phase 3: receives a `contextProposal` frame directly — the `context` target's
   * `create_context` walk. The host owns turning this into a ghost overlay on its own canvas
   * (there is no thread card for it, unlike `contextConfirmation`'s confirm/cancel flow).
   */
  onContextProposal?: (proposal: ComposerAgentContextConfirmation) => void;
  /**
   * EAINT-895 Phase 5: receives a `typeEditProposal` frame directly — the `schema` target's
   * `propose_type_edit` ghost. Same pass-through shape as {@link onContextProposal}: the host owns
   * turning this into a ghost in its own section (there is no thread card for it, and no separate
   * write route — a kept ghost applies through the host's existing save path).
   */
  /** A `propose_type_edit` frame, stamped with the turn that produced it so the host's ghost can
   * say where it came from (`/standards/agentic/agentic-ux-standards.md` Rule 7). */
  onTypeEditProposal?: (proposal: ComposerAgentTypeEditProposal, provenance: ComposerGhostProvenance) => void;
  /**
   * Reports the availability this organism itself determines (the probe on mount, and any
   * unreachable-service failure thereafter) — so a host that offers this thread behind its own
   * affordance (a tab, a toggle) can hide that affordance entirely rather than mount a
   * permanently-empty panel (`/standards/agentic/agentic-ux-standards.md`: "agent affordances
   * hide rather than grey out; availability is probed, not assumed"). Optional: a host that
   * always shows this organism inline (no affordance to hide) has nothing to wire this to.
   */
  onAvailabilityChange?: (available: boolean) => void;
  /** "New with agent" deep link — focuses the input on mount (gallery flow). */
  focusOnMount?: boolean;
  /**
   * Jumps to the composer section a turn names (`item.block`). Without it a turn carrying a block
   * renders no jump link — the affordance is dropped rather than pointed at nothing.
   */
  onJumpToBlock?: (blockId: string) => void;
  /**
   * Seeds the thread. The type composer's side rail uses it to mount a conversation a story or a
   * test can read without a live service; a host with a live endpoint leaves it unset and the
   * thread fills from the stream.
   */
  initialItems?: AgentThreadItem[];
  /**
   * Contract datum ids for a host whose decomposition covers this thread (the type composer's side
   * rail: `type-composer.jsx:1871-1898`). Unset elsewhere, and then nothing is stamped.
   */
  parity?: AgentThreadParity;
  /**
   * Overrides the compose bar's placeholder. The default asks what to BUILD, which is the gallery's
   * question; a host editing something that already exists asks what to CHANGE
   * (`type-composer.jsx:1896`).
   */
  inputPlaceholder?: string;
  className?: string;
  /**
   * The agent's ambient critique, undismissed, across every block — renders as "Watching as you
   * edit" (`type-rail/trail-composer.jsx:1879-1889`) between the thread and the compose bar.
   * Absent or empty renders nothing, same as a host with no critique to show.
   */
  critique?: ComposerTypeCritique[];
  /** Dismisses one critique item — the chip's own remove control. */
  onDismissCritique?: (id: string) => void;
}

/** Imperative handle so a ghost row outside this organism (spec Keyboard map: "Escape from a
 * ghost returns to the thread") can move focus back into the message input without this organism
 * needing to know anything about where its ghosts are actually rendered. */
export interface AgentThreadHandle {
  focusInput: () => void;
  /** Brings the named turn into view and focuses it — the return leg of a ghost's provenance line.
   *  Returns `false` when the turn is no longer in this thread, so the caller can say so. */
  focusTurn: (turnId: string) => boolean;
  /**
   * Submits `message` as though the author typed and sent it — the "Draft a new aspect" dialog's
   * `describe` route uses this to hand its intent to the agent rather than writing anything
   * directly (EAINT-933 Phase 3.4): the agent's own `propose_type_edit` tool call is what turns
   * it into a reviewable ghost, same as any other agent-proposed edit. A no-op while `endpoint`
   * is absent, same guard `sendTurn` already applies to a typed submit.
   */
  sendMessage: (message: string) => void;
}

/** The contract ids the host's decomposition gives this thread's own elements. */
export interface AgentThreadParity extends AgentMessageParity {
  /** Container path the thread list sits in, e.g. `side > panel#2 > list#2`. */
  threadContainer: string;
  /** Container path the compose bar sits in, e.g. `side > panel#2 > group`. */
  composeContainer: string;
  /** Datum id for the message input. */
  input: string;
  /** Datum id for the send button. */
  send: string;
  /** Datum id for the "Watching as you edit" card. */
  critiqueCard: string;
  /** Datum id for the card's heading. */
  critiqueHeading: string;
  /** Datum id for one critique chip. */
  critiqueChip: string;
  /** Datum id for a chip's dismiss control. */
  critiqueDismiss: string;
}

/**
 * The composer's agent thread (SC8): streams chat + `a2uiProposal` frames over SSE with real JWT
 * auth, renders proposals as accept/reject cards that route into the ghost buffer, and shows a
 * friendly unavailable state (proactively probed on mount, and reactively on a connection failure)
 * so the rest of the composer stays fully functional without it.
 */
const AgentThreadOrganism = forwardRef<AgentThreadHandle, AgentThreadOrganismProps>(({
  endpoint,
  getToken,
  target,
  scope,
  draftDigest,
  onProposeGhost,
  onAcceptGhost,
  onRejectGhost,
  onMessageSent,
  onGhostAccepted,
  onGhostRejected,
  onContextConfirmed,
  onContextCancelled,
  onContextProposal,
  onTypeEditProposal,
  onAvailabilityChange,
  focusOnMount = false,
  onJumpToBlock,
  initialItems,
  parity,
  inputPlaceholder,
  className = '',
  critique,
  onDismissCritique,
}, ref) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<AgentThreadItem[]>(initialItems ?? []);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const itemsRef = useRef<AgentThreadItem[]>([]);
  /** In-flight `POST /context/confirm` requests, keyed by thread-item id, so unmount can abort them. */
  const confirmControllersRef = useRef<Map<string, AbortController>>(new Map());
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const conversationId = useMemo(() => crypto.randomUUID(), []);

  itemsRef.current = items;

  useEffect(() => {
    if (!endpoint) return undefined;
    const controller = new AbortController();
    probeComposerAgentAvailability(endpoint, controller.signal).then((ok) => {
      if (!ok) setUnavailable(true);
    });
    return () => controller.abort();
  }, [endpoint]);

  // A thread that already holds turns stays available: an outage must not delete the conversation
  // the user is reading (conversational-ai-ux § Rules 5 and 7 — context survives a failure). The
  // outage is reported as a turn in the thread instead, and the host keeps its affordance mounted.
  useEffect(() => {
    onAvailabilityChange?.((!!endpoint && !unavailable) || itemsRef.current.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, unavailable, items.length]);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      inputRef.current?.focus();
    },
    focusTurn: (turnId: string) => {
      const turn = messagesRef.current?.querySelector<HTMLElement>(`[data-turn-id="${turnId}"]`);
      // Reports the miss rather than swallowing it. A ghost outlives the thread that proposed
      // it, so a provenance link CAN name a turn that is no longer here — and a link that
      // silently does nothing asserts an inspectability the surface does not have.
      if (!turn) return false;
      turn.scrollIntoView?.({ block: 'nearest' });
      // The turn is prose, not a control: it takes focus only for this move, so a keyboard user
      // following the provenance link lands on the message rather than past it.
      turn.tabIndex = -1;
      turn.focus();
      return true;
    },
    sendMessage: (message: string) => {
      void sendTurn(message);
    },
  }));

  useEffect(
    () => () => {
      abortRef.current?.abort();
      confirmControllersRef.current.forEach((c) => c.abort());
      confirmControllersRef.current.clear();
    },
    []
  );

  const sendTurn = useCallback(
    async (message: string) => {
      if (!endpoint) {
        setUnavailable(true);
        // Say so in the thread rather than dropping the turn silently — a message that vanishes
        // with no reply reads as the agent ignoring the user. The cause is known here (no service
        // URL is configured), so it is named instead of reported as an unknown failure.
        setItems((prev) => [
          ...prev,
          { kind: 'chat', id: crypto.randomUUID(), role: 'assistant', content: translateAgentError(t, 'not_configured'), isError: true },
        ]);
        return;
      }

      const history = itemsRef.current
        .filter((item): item is Extract<AgentThreadItem, { kind: 'chat' }> => item.kind === 'chat')
        .map((item) => ({ role: item.role, content: item.content }));

      const userItem: AgentThreadItem = { kind: 'chat', id: crypto.randomUUID(), role: 'user', content: message };
      const assistantId = crypto.randomUUID();
      setItems((prev) => [...prev, userItem, { kind: 'chat', id: assistantId, role: 'assistant', content: '' }]);
      setIsLoading(true);
      setLastMessage(message);

      const controller = new AbortController();
      abortRef.current = controller;
      let assistantText = '';

      try {
        await sendComposerAgentMessage({
          endpoint,
          message,
          conversationHistory: history,
          conversationId,
          target,
          scope,
          draftDigest,
          getToken,
          signal: controller.signal,
          onChatText: (text) => {
            assistantText += text;
            setItems((prev) =>
              prev.map((it) => (it.kind === 'chat' && it.id === assistantId ? { ...it, content: assistantText } : it))
            );
          },
          onProgress: (tool, status) => setActiveTool(status === 'calling' ? tool : null),
          onProposal: (proposal) => {
            const components = proposalToGhostComponents(proposal);
            const ghostId = crypto.randomUUID();
            onProposeGhost?.({ id: ghostId, components }, proposal.rationale);
            setItems((prev) => [
              ...prev,
              {
                kind: 'proposal',
                id: crypto.randomUUID(),
                ghostId,
                rationale: proposal.rationale,
                componentCount: components.length,
                status: 'pending',
              },
            ]);
          },
          onQuestion: (questionSet: ComposerAgentQuestionSet) => {
            setItems((prev) => [
              ...prev,
              { kind: 'question', id: crypto.randomUUID(), questions: questionSet.questions, status: 'pending' },
            ]);
          },
          onConfirmContext: (confirmation: ComposerAgentContextConfirmation) => {
            setItems((prev) => [
              ...prev,
              { kind: 'contextConfirmation', id: crypto.randomUUID(), confirmation, status: 'pending' },
            ]);
          },
          onContextProposal: (proposal: ComposerAgentContextConfirmation) => {
            onContextProposal?.(proposal);
          },
          onTypeEditProposal: (proposal: ComposerAgentTypeEditProposal) => {
            // Provenance is observed here, not taken from the frame: the turn being answered and
            // the moment the frame arrived are facts this client holds, and the agent does not get
            // to assert its own origin (Agentic UX Rule 7).
            onTypeEditProposal?.(proposal, {
              turnId: userItem.id,
              prompt: message,
              at: new Date().toISOString(),
            });
          },
          onError: (code) => {
            setItems((prev) => [...prev, { kind: 'chat', id: crypto.randomUUID(), role: 'assistant', content: translateAgentError(t, code), isError: true }]);
          },
          onDone: () => setActiveTool(null),
        });
        setUnavailable(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        // A turn that failed before any text arrived leaves an empty bubble above the error, which
        // reads as a reply the agent gave. Drop it; a partial answer is kept.
        setItems((prev) => prev.filter((it) => !(it.kind === 'chat' && it.id === assistantId && it.content === '')));
        if (err instanceof ComposerAgentNetworkError) {
          setUnavailable(true);
          // A thread that already holds turns never swaps to the unavailable empty state, so the
          // outage has to be reported IN the thread — and named, since being unable to reach the
          // service is a different situation from the agent failing to answer.
          setItems((prev) => [
            ...prev,
            { kind: 'chat', id: crypto.randomUUID(), role: 'assistant', content: translateAgentError(t, 'unreachable'), isError: true },
          ]);
        } else {
          setItems((prev) => [
            ...prev,
            { kind: 'chat', id: crypto.randomUUID(), role: 'assistant', content: translateAgentError(t, 'unknown'), isError: true },
          ]);
        }
      } finally {
        setIsLoading(false);
        setActiveTool(null);
        abortRef.current = null;
      }
    },
    [endpoint, conversationId, target, scope, draftDigest, getToken, onProposeGhost, onContextProposal, onTypeEditProposal, t]
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    onMessageSent?.();
    sendTurn(trimmed);
  }, [input, isLoading, onMessageSent, sendTurn]);

  const handleRetry = useCallback(() => {
    if (lastMessage) sendTurn(lastMessage);
  }, [lastMessage, sendTurn]);

  const handleAccept = useCallback(
    (ghostId: string) => {
      onAcceptGhost?.(ghostId);
      setItems((prev) =>
        prev.map((it) => {
          if (it.kind !== 'proposal' || it.ghostId !== ghostId) return it;
          onGhostAccepted?.(it.componentCount);
          return { ...it, status: 'accepted' as const };
        })
      );
    },
    [onAcceptGhost, onGhostAccepted]
  );

  const handleReject = useCallback(
    (ghostId: string) => {
      onRejectGhost?.(ghostId);
      setItems((prev) =>
        prev.map((it) => {
          if (it.kind !== 'proposal' || it.ghostId !== ghostId) return it;
          onGhostRejected?.(it.componentCount);
          return { ...it, status: 'rejected' as const };
        })
      );
    },
    [onRejectGhost, onGhostRejected]
  );

  const handleSubmitAnswers = useCallback(
    (itemId: string, composedMessage: string) => {
      setItems((prev) =>
        prev.map((it) => (it.kind === 'question' && it.id === itemId ? { ...it, status: 'answered' as const, answerSummary: composedMessage } : it))
      );
      sendTurn(composedMessage);
    },
    [sendTurn]
  );

  const handleConfirmContext = useCallback(
    async (itemId: string) => {
      if (!endpoint) return;
      const item = itemsRef.current.find((it) => it.id === itemId);
      if (!item || item.kind !== 'contextConfirmation') return;
      // Guard on status, not just kind: `itemsRef` only refreshes on render, so without this a
      // double-dispatched click would issue a second POST and create a duplicate context.
      // `error` is re-confirmable because nothing was written.
      if (item.status !== 'pending' && item.status !== 'error') return;

      const controller = new AbortController();
      confirmControllersRef.current.set(itemId, controller);
      setItems((prev) =>
        prev.map((it) => (it.kind === 'contextConfirmation' && it.id === itemId ? { ...it, status: 'confirming' as const } : it))
      );
      try {
        const result = await confirmComposerContext({ endpoint, confirmation: item.confirmation, getToken, signal: controller.signal });
        setItems((prev) =>
          prev.map((it) =>
            it.kind === 'contextConfirmation' && it.id === itemId
              ? { ...it, status: 'confirmed' as const, systemName: result.systemName, errorCode: undefined }
              : it
          )
        );
        onContextConfirmed?.(result.systemName);
      } catch (err) {
        if (controller.signal.aborted) return;
        const errorCode = contextConfirmErrorCode(err);
        setItems((prev) =>
          prev.map((it) => (it.kind === 'contextConfirmation' && it.id === itemId ? { ...it, status: 'error' as const, errorCode } : it))
        );
      } finally {
        confirmControllersRef.current.delete(itemId);
      }
    },
    [endpoint, getToken, onContextConfirmed]
  );

  const handleCancelContext = useCallback(
    (itemId: string) => {
      setItems((prev) => prev.map((it) => (it.id === itemId && it.kind === 'contextConfirmation' ? { ...it, status: 'rejected' as const } : it)));
      onContextCancelled?.();
    },
    [onContextCancelled]
  );

  /**
   * EAINT-895 Phase 5 (also generally applicable, spec § Turn state and stop, Rule 8): abort the
   * in-flight turn. Already-landed ghosts/proposals stay exactly as they are — aborting only
   * stops the model from continuing, it never rolls back what already streamed.
   */
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setIsLoading(false);
    setActiveTool(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if ((!endpoint || unavailable) && items.length === 0) {
    return (
      <div className={['agent-thread', 'agent-thread--unavailable', className].filter(Boolean).join(' ')} data-testid="agent-thread-unavailable">
        <EmptyStateAtom icon="pi-comments" title={t('composer.agent.unavailableTitle')} description={t('composer.agent.unavailableDescription')} />
        {endpoint && lastMessage && (
          <ButtonAtom variant="text" size="small" onClick={handleRetry}>
            {t('composer.agent.retry')}
          </ButtonAtom>
        )}
      </div>
    );
  }

  return (
    <div className={['agent-thread', className].filter(Boolean).join(' ')} data-testid="agent-thread">
      <div ref={messagesRef} className="agent-thread__messages" data-testid="agent-thread-messages" data-container={parity?.threadContainer}>
        {items.length === 0 && (
          <EmptyStateAtom icon="pi-sparkles" title={t('composer.agent.emptyTitle')} description={t('composer.agent.emptyDescription')} />
        )}
        {items.map((item) => (
          <AgentMessageMolecule
            key={item.id}
            item={item}
            onAccept={handleAccept}
            onReject={handleReject}
            onSubmitAnswers={handleSubmitAnswers}
            onConfirmContext={handleConfirmContext}
            onCancelContext={handleCancelContext}
            onJumpToBlock={onJumpToBlock}
            parity={parity}
          />
        ))}
        {activeTool && (
          <div className="agent-thread__tool-progress" data-testid="agent-thread-tool-progress">
            {t('composer.agent.toolProgress', { tool: activeTool })}
          </div>
        )}
        {isLoading && !activeTool && (
          <div className="agent-thread__thinking" data-testid="agent-thread-thinking">
            {t('composer.agent.thinking')}
          </div>
        )}
      </div>
      {/*
        `side.watching-as-you-edit` (`type-rail/trail-composer.jsx:1879-1889`, `.t-critique`) —
        the agent's ambient critique, undismissed, across every block. Advisory only (spec §
        Agentic UX): reads and comments, never writes; acting on a critique goes through the
        existing ghost flow, not through this chip. `aria-live="polite"` per the spec's "Watching
        as you edit" status standard — a new critique is announced without stealing focus.
      */}
      {critique && critique.length > 0 && (
        <div
          className="agent-thread__critique"
          data-testid="agent-thread-critique"
          aria-live="polite"
          data-datum={parity?.critiqueCard}
          data-kind="card"
        >
          <div className="agent-thread__critique-heading" data-datum={parity?.critiqueHeading} data-kind="heading">
            {t('composer.agent.watching')}
          </div>
          {critique.map((c) => (
            <ChipAtom
              key={c.id}
              label={c.text}
              className={`agent-thread__critique-chip agent-thread__critique-chip--${c.severity}`}
              data-testid={`agent-thread-critique-${c.id}`}
              data-datum={parity?.critiqueChip}
              data-kind="chip"
              template={
                <>
                  {/* Severity is carried by an icon and a (visually hidden) word as well as the
                      chip's tone, so it never rests on colour alone (ux-craft A6). */}
                  <i className={`agent-thread__critique-icon pi ${CRITIQUE_SEVERITY_ICON[c.severity]}`} aria-hidden="true" />
                  <span className="agent-thread__critique-severity">
                    {t(`composer.agent.critiqueSeverity.${c.severity}`)}
                  </span>
                  <span className="p-chip-text">{c.text}</span>
                  <button
                    type="button"
                    className="p-chip-remove-icon agent-thread__critique-dismiss pi pi-times-circle"
                    aria-label={t('composer.agent.dismissCritique', { text: c.text })}
                    onClick={() => onDismissCritique?.(c.id)}
                    data-datum={parity?.critiqueDismiss}
                    data-kind="button"
                  />
                </>
              }
            />
          ))}
        </div>
      )}
      <div className="agent-thread__input" data-container={parity?.composeContainer}>
        <TextareaAtom
          ref={inputRef}
          value={input}
          placeholder={inputPlaceholder ?? t('composer.agent.placeholder')}
          aria-label={t('composer.agent.inputLabel')}
          data-datum={parity?.input}
          data-kind="textarea"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={isLoading}
        />
        {isLoading ? (
          <ButtonAtom variant="secondary" size="small" onClick={handleStop} data-testid="agent-thread-stop">
            {t('composer.agent.stop')}
          </ButtonAtom>
        ) : (
          <ButtonAtom
            variant="primary"
            size="small"
            className="agent-thread__send"
            disabled={!input.trim()}
            onClick={handleSend}
            data-datum={parity?.send}
            data-kind="button"
          >
            {t('composer.agent.send')}
          </ButtonAtom>
        )}
      </div>
    </div>
  );
});

AgentThreadOrganism.displayName = 'AgentThreadOrganism';

export default AgentThreadOrganism;
